import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Tent } from 'lucide-react';



const AddLocationForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: '',
        latitude: '',
        longitude: '',
        otg_route: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Populate form with selected coordinates from map
    useEffect(() => {
        const selectedLat = searchParams.get('selectedLat');
        const selectedLng = searchParams.get('selectedLng');
        
        if (selectedLat && selectedLng) {
            const lat = parseFloat(selectedLat);
            const lng = parseFloat(selectedLng);
            
            // Validate coordinates before setting
            if (!isNaN(lat) && !isNaN(lng) && 
                lat >= -90 && lat <= 90 && 
                lng >= -180 && lng <= 180) {
                
                setFormData(prev => ({
                    ...prev,
                    latitude: lat.toString(),
                    longitude: lng.toString()
                }));
                
                console.log('📍 Pre-populated coordinates from map:', {
                    lat: lat,
                    lng: lng
                });
            }
        }
    }, [searchParams]);

    // Common options for dropdowns
    const typeOptions = [
        'campground',
        'dispersed',
        'group camp',
        'no camping',
        'rv park',
        'stealth camp'
    ];

    const iconCategoryOptions = [
        'food', 'lodging', 'entertainment', 'nature', 'culture', 'shopping', 'healthcare', 'education', 'transport'
    ];

    const importStatusOptions = [
        'pending', 'imported', 'failed', 'processing', 'verified'
    ];

    const validateForm = () => {
        const newErrors = {};

        // Required fields
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.type.trim()) newErrors.type = 'Type is required';

        // Latitude validation (-90 to 90)
        const lat = parseFloat(formData.latitude);
        if (isNaN(lat) || lat < -90 || lat > 90) {
            newErrors.latitude = 'Latitude must be between -90 and 90';
        }

        // Longitude validation (-180 to 180)
        const lng = parseFloat(formData.longitude);
        if (isNaN(lng) || lng < -180 || lng > 180) {
            newErrors.longitude = 'Longitude must be between -180 and 180';
        }

        // Name length validation
        if (formData.name.length > 255) {
            newErrors.name = 'Name must be 255 characters or less';
        }

        // Type length validation
        if (formData.type.length > 100) {
            newErrors.type = 'Type must be 100 characters or less';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async () => {

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            // Convert string coordinates to numbers for submission
            const submitData = {
                ...formData,
                latitude: parseFloat(formData.latitude),
                longitude: parseFloat(formData.longitude)
            };

            // Make API call to your PHP service
            const response = await fetch('/otgservices/AddOTGCampingLocation.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(submitData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to add location');
            }

            const result = await response.json();

            const locationId = result.id;

            // Get return navigation parameters to pass to CampDetailsEditor
            const returnLat = searchParams.get('returnLat');
            const returnLng = searchParams.get('returnLng');
            const returnZoom = searchParams.get('returnZoom');
            const returnFrom = searchParams.get('returnFrom');
            
            // Build URL with return parameters for CampDetailsEditor
            const baseUrl = `/events/camps/${locationId}/edit/5`;
            const params = new URLSearchParams();
            
            if (returnLat && returnLng && returnFrom === 'map') {
                params.set('returnLat', returnLat);
                params.set('returnLng', returnLng);
                if (returnZoom) params.set('returnZoom', returnZoom);
                params.set('returnFrom', 'addLocation');
                
                console.log('🔗 Passing return coordinates to CampDetailsEditor:', {
                    returnLat, returnLng, returnZoom, returnFrom
                });
            }
            
            const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
            navigate(url);

            // Show success message
            alert('Location added successfully!');


        } catch (error) {
            console.error('Error submitting form:', error);
            alert('Error adding location: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        // Get return navigation parameters
        const returnLat = searchParams.get('returnLat');
        const returnLng = searchParams.get('returnLng');
        const returnZoom = searchParams.get('returnZoom');
        const returnFrom = searchParams.get('returnFrom');

        // Reset form
        setFormData({
            name: '',
            description: '',
            type: '',
            latitude: '',
            longitude: '',
            otg_route: ''
        });

        // Clear any errors
        setErrors({});

        // Navigate back to map with original position if available
        if (returnLat && returnLng && returnFrom === 'map') {
            const params = new URLSearchParams();
            params.set('lat', returnLat);
            params.set('lng', returnLng);
            if (returnZoom) params.set('zoom', returnZoom);
            params.set('from', 'addLocation');
            
            navigate(`/events/campmap?${params.toString()}`);
        } else {
            navigate(-1);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-orange-600 text-white px-6 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-white p-2 rounded">
                            <Tent className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold">OTG Camp</h1>
                            <p className="text-sm opacity-90">OFF THE GRID</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <span className="text-sm bg-orange-700 px-2 py-1 rounded">Pro</span>
                    </div>
                </div>
            </div>

            <div className="max-w-2xl mx-auto p-6">
                <div className="bg-white rounded-lg shadow-lg p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">Add New Location</h2>
                    </div>

            <div className="space-y-4">
                {/* Name */}
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Name *
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.name ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter location name"
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>

                {/* Type */}
                <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                        Type *
                    </label>
                    <select
                        id="type"
                        name="type"
                        value={formData.type}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.type ? 'border-red-500' : 'border-gray-300'
                        }`}
                    >
                        <option value="">Select a type</option>
                        {typeOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                    {errors.type && <p className="mt-1 text-sm text-red-600">{errors.type}</p>}
                </div>

                {/* Coordinates Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="latitude" className="block text-sm font-medium text-gray-700 mb-1">
                            Latitude *
                        </label>
                        <input
                            type="number"
                            id="latitude"
                            name="latitude"
                            value={formData.latitude}
                            onChange={handleChange}
                            step="any"
                            min="-90"
                            max="90"
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors.latitude ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="e.g., 37.7749"
                        />
                        {errors.latitude && <p className="mt-1 text-sm text-red-600">{errors.latitude}</p>}
                    </div>

                    <div>
                        <label htmlFor="longitude" className="block text-sm font-medium text-gray-700 mb-1">
                            Longitude *
                        </label>
                        <input
                            type="number"
                            id="longitude"
                            name="longitude"
                            value={formData.longitude}
                            onChange={handleChange}
                            step="any"
                            min="-180"
                            max="180"
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors.longitude ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="e.g., -122.4194"
                        />
                        {errors.longitude && <p className="mt-1 text-sm text-red-600">{errors.longitude}</p>}
                    </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex gap-3 pt-4">
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className={`flex-1 py-2 px-4 rounded-md text-white font-medium ${
                            isSubmitting
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
                        }`}
                    >
                        {isSubmitting ? 'Adding Location...' : 'Add Location'}
                    </button>

                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                        className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                        Cancel
                    </button>
                </div>
            </div>
                </div>
            </div>
        </div>
    );
};



export default AddLocationForm;